import {
  Breadcrumbs,
  BreadcrumbItem,
  Button,
  Divider,
  Link,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router';
import { parse } from 'node-html-parser';
import { RefObject, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useReactToPrint } from 'react-to-print';
import './styles/ed-v3.css';

import useSegment from '@/hooks/useSegment';
import {
  breadcrumbs,
  educationParams,
  EducationParams,
} from '@/patient-app-common/helpers/education_helpers';
import { Education } from '@/patient-app-common/types/Education';

import { EduFeedback } from './EduFeedback';

const EDUCATION_EVENT = 'Education Topic Viewed';

interface EduTopicProps {
  contentContainerRef: RefObject<HTMLDivElement | null>;
  topicId: string;
  education: Education[];
  setSidebarOverlaps: (boolean: boolean) => void;
  pageWidth: number;
}

export function EduTopic({
  contentContainerRef,
  education,
  topicId,
  setSidebarOverlaps,
  pageWidth,
}: EduTopicProps) {
  const { t } = useTranslation('education');
  const { sendTrackEvent } = useSegment();

  const contentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef });

  const navigate = useNavigate();

  useEffect(() => {
    if (!topicId) return;

    if (contentContainerRef.current) {
      contentContainerRef.current.focus();
      contentContainerRef.current.scrollTop = 0;
    }
  }, [contentContainerRef, topicId]);

  const eduTopic = useMemo(() => {
    if (!education.length || !topicId) return null;
    return educationParams(education, _.toNumber(topicId));
  }, [education, topicId]);

  const html = useMemo(() => {
    if (!eduTopic?.html) return '';

    const root = parse(eduTopic.html);
    const linkElements = root.querySelectorAll(
      'a[href*="seamless.md/#/education/"]'
    );
    linkElements.forEach((element) => {
      element.removeAttribute('target');
    });

    return root.toString();
  }, [eduTopic]);

  useEffect(() => {
    if (!eduTopic) return;

    sendTrackEvent(EDUCATION_EVENT, {
      title: eduTopic.title,
      id: topicId,
      source: 'EduTopic',
    });
  }, [eduTopic, topicId]);

  const topicsTrail = breadcrumbs(education, topicId);
  const isSmallScreen = pageWidth < 768;

  if (!eduTopic) return null;

  if (eduTopic.html) {
    return (
      <div
        className="flex-1 overflow-auto focus:outline-none"
        ref={contentContainerRef}
        tabIndex={0}
      >
        {isSmallScreen && (
          <div className="lg:hidden sticky top-0 z-10 bg-primary-50 border-b-2 border-primary-200 px-4 py-3 shadow-sm">
            <div className="flex items-center gap-3">
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={() => {
                  setSidebarOverlaps(true);
                  navigate('/education');
                }}
              >
                <Icon icon="mdi:arrow-left" className="text-2xl" />
              </Button>
              <h1 className="text-lg font-semibold line-clamp-1 flex-1 truncate">
                {eduTopic.title}
              </h1>
            </div>
          </div>
        )}
        <div className="flex flex-col md:flex-row items-start justify-between gap-2 m-8 mb-0">
          <Breadcrumbs color="primary" size="lg" variant="solid">
            {topicsTrail.reverse().map((topic) => (
              <BreadcrumbItem href={`/education/${topic.id}`}>
                {isSmallScreen
                  ? _.truncate(topic.title, {
                      length: 40,
                      separator: ' ',
                      omission: '...',
                    })
                  : topic.title}
              </BreadcrumbItem>
            ))}
          </Breadcrumbs>
          <Button
            color="primary"
            endContent={<Icon icon="mdi:printer" className="text-lg" />}
            onPress={() => handlePrint()}
            variant="faded"
          >
            {t('common.action.print')}
          </Button>
        </div>
        <div className="max-w-7xl mx-auto p-8 pt-3">
          <div
            className="ed-v3"
            dangerouslySetInnerHTML={{ __html: html }}
            ref={contentRef}
          />
        </div>
        <Divider />
        <EduLinks education={education} topicId={topicId} />
        <EduFeedback education={education} topicId={topicId} />
      </div>
    );
  }

  if (!_.isEmpty(eduTopic.subtopics)) {
    return (
      <div
        className="flex-1 overflow-auto focus:outline-none"
        ref={contentContainerRef}
        tabIndex={0}
      >
        <div className="max-w-7xl mx-auto p-12">
          <EduSubtopics topic={eduTopic} />
        </div>
      </div>
    );
  }
}

function EduSubtopics({ topic }: { topic: EducationParams }) {
  return (
    <div>
      <h1 className="font-semibold text-2xl mb-4">{topic.title}</h1>
      <div className="ml-4 space-y-2">
        {topic.subtopics &&
          topic.subtopics.map((subtopic: Education) => (
            <Link className="block" href={`/education/${subtopic.id}`}>
              {subtopic.title}
            </Link>
          ))}
      </div>
    </div>
  );
}

function EduLinks({
  topicId,
  education,
}: {
  topicId: string;
  education: Education[];
}) {
  const { t } = useTranslation('education');

  const nextTopic = useMemo(() => {
    if (!topicId) return;

    const htmlTopics = education.filter((topic) => topic.html);
    const id = _.toNumber(topicId);
    const index = _.findIndex(htmlTopics, ['id', id]);
    return htmlTopics[index + 1];
  }, [education, topicId]);

  const prevTopic = useMemo(() => {
    if (!topicId) return;

    const htmlTopics = education.filter((topic) => topic.html);
    const id = _.toNumber(topicId);
    const index = _.findIndex(htmlTopics, ['id', id]);
    return htmlTopics[index - 1];
  }, [education, topicId]);

  if (!topicId) return null;

  return (
    <div className="flex flex-col mt-4 mx-8">
      {!_.isEmpty(prevTopic) && (
        <Link href={`/education/${prevTopic.id}`}>
          {t('edulink.button.back', { title: prevTopic.title })}
        </Link>
      )}
      {!_.isEmpty(nextTopic) && (
        <Link href={`/education/${nextTopic.id}`}>
          {t('edulink.button.next', { title: nextTopic.title })}
        </Link>
      )}
    </div>
  );
}
